import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import config from 'config';

import {
  OPENED,
  COMPLETED,
  AVAILABLE_CLOSED_APPOINTMENT_STATUSES,
} from 'modules/common/constants/appointmentStatuses';
import { Icon, IconWithDotBadge } from 'shared/components';
import { HOME_VISIT } from 'modules/queues/constants/F2FTypes';
import { SCHEDULED, WALK_IN } from 'modules/queues/constants/queueTypes';
import ExpectedTimer from 'modules/queues/common/components/ExpectedTimer';
import { getCloseTimeFormatted, getScheduleTimeFormatted } from 'shared/utils/dateTimeUtils';
import { APPOINTMENT_STATUS_TITLES } from 'modules/queues/constants/appointmentStatusTitles';

import theme from './AppointmentSchedule.scss';


const isClalit = config.product_name === 'clalit';

const propTypes = {
  closeTime: PropTypes.string,
  expectedTime: PropTypes.object,
  isF2F: PropTypes.bool.isRequired,
  faceToFaceType: PropTypes.string,
  overdue: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    canSeeSchedule: PropTypes.bool.isRequired,
    canSeeStatus: PropTypes.bool.isRequired,
  }).isRequired,
  schedules: PropTypes.arrayOf(PropTypes.shape({
    startTime: PropTypes.string,
    endTime: PropTypes.string,
  })),
  queueType: PropTypes.oneOf([WALK_IN, SCHEDULED]),
  prerequisitesIncomplete: PropTypes.bool.isRequired,
};

const defaultProps = {
  schedules: null,
  closeTime: null,
  queueType: null,
  expectedTime: null,
  faceToFaceType: null,
};

const iconPropTypes = {
  isF2F: PropTypes.bool.isRequired,
  isAppointmentClosed: PropTypes.bool.isRequired,
  canSeeStatus: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired,
  faceToFaceType: PropTypes.string,
  queueType: PropTypes.oneOf([WALK_IN, SCHEDULED]),
};

const iconDefaultProps = {
  queueType: null,
  faceToFaceType: null,
};
// @todo move to separate file
const AppointmentStatusIcon = ({ queueType, status, isAppointmentClosed, canSeeStatus, isF2F, faceToFaceType }) => (
  <div>
    <Choose>
      <When condition={ isAppointmentClosed }>
        <If condition={ canSeeStatus }>
          <IconWithDotBadge
            name={ APPOINTMENT_STATUS_TITLES[status].iconName }
            color={ status === COMPLETED ? 'green' : 'red' }
          />
        </If>
      </When>
      <When condition={ isF2F && faceToFaceType === HOME_VISIT }>
        <IconWithDotBadge name="home" />
      </When>
      <When condition={ isF2F }>
        <IconWithDotBadge name="two-people" />
      </When>
      <When condition={ isClalit }>
        <IconWithDotBadge name="vidcam" />
      </When>
      <When condition={ queueType === WALK_IN }>
        <IconWithDotBadge name="characters-shrink" />
      </When>
      <When condition={ queueType === SCHEDULED }>
        <IconWithDotBadge name="calendar" />
      </When>
    </Choose>
  </div>
);

AppointmentStatusIcon.propTypes = iconPropTypes;
AppointmentStatusIcon.defaultProps = iconDefaultProps;


const AppointmentSchedule = ({
  intl,
  isF2F,
  status,
  overdue,
  schedules,
  queueType,
  closeTime,
  permissions,
  expectedTime,
  faceToFaceType,
  prerequisitesIncomplete,
}) => {
  const isAppointmentClosed = AVAILABLE_CLOSED_APPOINTMENT_STATUSES.includes(status);
  const { canSeeStatus } = permissions;
  const { startTime, endTime } = schedules[0];
  const appointmentStatusIconProps = { queueType, status, isAppointmentClosed, canSeeStatus, isF2F, faceToFaceType };

  return (
    <div className={ theme.root }>
      <AppointmentStatusIcon { ...appointmentStatusIconProps } />
      <div className={ theme.body }>
        <Choose>
          <When condition={ isAppointmentClosed }>
            <Fragment>
              <span className={ status === COMPLETED ? theme.highlightedGreen : theme.highlightedRed }>
                <FormattedMessage { ...APPOINTMENT_STATUS_TITLES[status].title } />
              </span>

              <If condition={ closeTime }>
                { getCloseTimeFormatted(closeTime, intl) }
              </If>
            </Fragment>
          </When>

          <When condition={ isClalit && queueType === WALK_IN && status === OPENED && !expectedTime.formattedDisplayMessage }>
            <div className={ classnames(theme.queueWalkIn, theme.clalitQueueWalkIn) }>
              <FormattedMessage id="Appointments.WalkInIndication.WaitingInQueue" defaultMessage="** Waiting in queue" />
            </div>
          </When>

          <When condition={ isClalit && queueType === WALK_IN }>

            <div className={ classnames(theme.queueWalkIn, theme.clalitQueueWalkIn) }>
              <FormattedMessage id="Appointments.PatientAppointmentCard.ETTBS.Title" defaultMessage="** ETTBS" />
            </div>
            <If condition={ expectedTime.formattedDisplayMessage && status === OPENED && !prerequisitesIncomplete }>
              <ExpectedTimer expectedTime={ expectedTime } />
            </If>
          </When>

          <When condition={ queueType === WALK_IN }>
            <div className={ theme.queueWalkIn }>
              <FormattedMessage id="Appointments.WalkInIndication.WaitingInQueue" defaultMessage="** Waiting in queue" />
            </div>
            <If condition={ expectedTime.formattedDisplayMessage && status === OPENED && !prerequisitesIncomplete }>
              <ExpectedTimer expectedTime={ expectedTime } />
            </If>
          </When>

          <When condition={ queueType === SCHEDULED }>
            <Fragment>
              <If condition={ expectedTime.formattedDisplayMessage && isClalit }>
                <div className={ classnames(theme.queueWalkIn, theme.clalitQueueWalkIn) }>
                  <FormattedMessage id="Appointments.PatientAppointmentCard.ETTBS.Title" defaultMessage="** ETTBS" />
                </div>
              </If>
              <div className={ theme.title }>
                { getScheduleTimeFormatted(startTime, endTime, intl) }
              </div>
              <Choose>

                <When condition={ expectedTime.formattedDisplayMessage && status === OPENED && !prerequisitesIncomplete }>
                  <ExpectedTimer expectedTime={ expectedTime } />
                </When>

                {/* FEM-29298 request to hide it, since "it works not as expected" */}
                {/* <When condition={ overdue }> */}
                {/*   <div className={ classnames(theme.subtitle, theme.status, theme.overdue) }> */}
                {/*     <Icon name="clock-outline" /> */}
                {/*     &nbsp; */}
                {/*     <FormattedMessage id="schedule.appointmentCard.overdue" defaultMessage="** Overdue" /> */}
                {/*   </div> */}
                {/* </When> */}
              </Choose>
            </Fragment>
          </When>
        </Choose>
      </div>
    </div>
  );
};

AppointmentSchedule.propTypes = propTypes;
AppointmentSchedule.defaultProps = defaultProps;

export default AppointmentSchedule;
